/* eslint-disable no-unused-vars */
import { useEffect, useState, forwardRef } from "react";
import { Dialog, Stack, useTheme, Grow } from "@mui/material";
import {
  WebsiteFormDialog,
  LoadingDialog,
  Notification,
  SuccessDialog,
  StickyIcon,
  ErrorDialog,
} from "../components";
import { fakeRandomProgress, waitWithProb } from "../common/Utils";
import useLocalStorage from "../common/hooks/useLocalStorage";
import {
  fetchUserExhibitingOrganisations,
  fetchIntelligentProfile,
  fetchExhibitorStatus,
} from "../common/api/requestApi";

const EXH_PROFILE_URL = "/profile/";

// -- Custom Dialog Grow in and out transition --
const Transition = forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} style={{ transformOrigin: "170% 27%" }} />;
});

// -- Main Component --
export default function Popup() {
  const theme = useTheme();
  const [state, setState] = useLocalStorage("ip-state", "");
  const [reusable, SetReusable] = useLocalStorage("ip-reusable", false);

  const [open, setOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [dialogId, setDialogId] = useState(state.dialogId ?? 0);
  const [isLoading, setIsLoading] = useState(state.dialogId == 1);

  const [website, setWebsite] = useState("");
  const [exhId, setExhId] = useState("");
  const [eveId, setEveId] = useState("");
  //const [isResponse, setIsResponse] = useState(false);

  // -- Loading variables --
  const estimatedLoadingTime = 60000; // 60 sec
  const refreshDelta = estimatedLoadingTime / 20;
  const [progress, setProgress] = useState(0);
  const [fakeProgress, setFakeProgress] = useState(0);
  const autoShowDelay = 2000; // 2 sec
  const completenessLimit = 70;

  // *** Handle Dialog open and close ***
  const handleClickOpen = () => {
    if (dialogId == 2 && window.location.href.includes(EXH_PROFILE_URL)) {
      if (reusable && dialogId == 2) setDialogId(0);
      else setOpenSnackbar(true);
    } else setOpen(true);
  };

  const handleClose = () => {
    if (!(dialogId == 1 && window.location.href.includes(EXH_PROFILE_URL)))
      setOpen(false);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // *** Handle Dialog navigationn and variables ***
  const handlePrevious = () => {
    setDialogId(0);
  };

  const handleSuccess = () => {
    setIsLoading(false);
    setDialogId(2);
  };

  const handleError = () => {
    setIsLoading(false);
    setDialogId(3);
  };

  const setProgresses = (value) => {
    setProgress(value);
    setFakeProgress(value);
  };

  // *** Handle Search functions ***
  const loadExhibitorInfo = async () => {
    // Load exhibitor info.
    const eveId = window.authSettings ? window.authSettings.eventEditionId : ""; //Stage ATM event: eve-7d50f206-6bd8-46ec-ad12-34b12760a7ef
    //const response = await fetchUserExhibitingOrganisations(eveId, "fr-fr");
    const response = await fetchUserExhibitingOrganisations(eveId, "en-gb");
    if (response.status > 200) {
      handleError();
      return;
    }
    // Get exhibitor Id and set both ids.
    const exhOrg = response.body.data.userExhibitingOrganisations.find(
      (org) => org.eventEditionId === eveId,
    );
    const exhId = exhOrg.exhibitingOrganisationId;
    setExhId(exhId);
    setEveId(eveId);
    return { eveId, exhId };
  };

  // Fetches exhId from the exhibitor's hub (either from html element or url).
  // Todo: Store ID and update componneents initial load.
  // Add listener on the dropdown list.
  // Add logic to hide popup if the event doesn't match the auth event (fetched in loadExhibitorInfo)
  const loadExhIdFromHub = () => {
    if (window.location.href.includes(EXH_PROFILE_URL)) {
      setExhId(window.location.href.split("/").pop());
      setEveId(window.location.href.split("/").pop());
    } else {
      const matches = document.querySelectorAll("select[data-dtm-attributes]");
      if (matches[0].options.length <= 1) return;
      const jsonResult = matches[0].dataset.dtmAttributes;
      if (jsonResult) setExhId(JSON.parse(jsonResult)["exhibitorId"] ?? exhId);
    }
  };

  const handleSearch = async ({ securedUrl }) => {
    console.log("Secured Url:");
    console.log(securedUrl);
    // reset progress for new search.
    setProgresses(0);
    // set IsLoading to trigger the useEffect (start the timer) and change visuals.
    setIsLoading(true);
    setDialogId(1);

    if (!(exhId && eveId)) {
      handleError();
      return;
    }

    // Request AI scrap
    try {
      const aiResponse = await fetchIntelligentProfile(securedUrl, exhId);
      console.log("🎉 Got result:", aiResponse);
      setIsLoading(false);
      setProgresses(100);
      // check if response is valid
      if (aiResponse[0]) {
        // Wait 2 sec for the progressbar to load then change dialog
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setDialogId(2);
      } else setDialogId(3); // show error dialog
    } catch (error) {
      console.error("🚨 Error:", error.message);
      setDialogId(3); // show error dialog
    }
  };

  // *** UseEffects ***
  // -- Initial state check on component's mount--
  useEffect(() => {
    const initialLoad = async () => {
      // Check completion with API
      const exhInfo = await loadExhibitorInfo();
      const checkResponse = await fetchExhibitorStatus(exhInfo.exhId);

      let isComplete = !checkResponse.show_popup; //OLD METHOD. checkResponse.completeness >= completenessLimit;
      console.log("Check - isComplete:");
      console.log(isComplete);
      // Allows to manually set to false in browser for testing purposes.
      // if (reusable) isComplete = false;

      // Allows to manually set to false in browser for testing purposes.
      // if (reusable) setIsActive(true);

      // - If Profile is complete after loading.
      if (isComplete && state && state.dialogId == 1) {
        // Was Loading, is now complete.
        setIsActive(true);
        setDialogId(2);
        setOpen(true);
      }
      // - If profile is complete (unnecessary: and sc has been used: && isComplete)
      else if (state?.dialogId == 2) {
        setIsActive(true);
        setOpen(false);
      }
      // - If profile is complete and dialogId == 0 (and not reusable)
      else if (isComplete && !reusable) setIsActive(false);
      // - If Profile is not yet complete.
      else {
        setIsActive(true);
        // -- If in Loading state --> resume progress then recheck
        if (state.dialogId == 1) {
          // -- Check window location
          if (window.location.href.includes(EXH_PROFILE_URL)) setOpen(true);
          // -- Check if still loading
          if (state.progressTimeStamp + estimatedLoadingTime > Date.now()) {
            // Is still Loading, show progress then recheck exhibitor's completion
            const p =
              ((Date.now() - state.progressTimeStamp) * 100) /
              estimatedLoadingTime;
            setProgress(p);
            // Wait for the estimated time of response (100%-actual progress)/100 * estimated time
            await waitWithProb(((100 - p) / 100) * estimatedLoadingTime, 1);
            // Re-check state.
            const response = await fetchExhibitorStatus(exhInfo.exhId);
            isComplete = response.status == "OK";
            isComplete ? handleSuccess() : handleError();
          } else handleError();
        } else {
          // -- If AI assistant has never been used.
          await waitWithProb(autoShowDelay, 1);
          setOpen(true);
        }
      }
    };

    initialLoad();
  }, []);

  // -- Progress Timer --
  useEffect(() => {
    if (isLoading) {
      const timer = setInterval(() => {
        fakeRandomProgress(
          fakeProgress,
          setFakeProgress,
          progress,
          setProgress,
        );
      }, refreshDelta);

      return () => {
        clearInterval(timer);
      };
    }
  }, [refreshDelta, fakeProgress, progress, isLoading]);

  // -- State update to Local Storage --
  useEffect(() => {
    const s = {
      dialogId: dialogId,
      progressTimeStamp:
        dialogId == 1 ? (state.progressTimeStamp ?? Date.now()) : null,
    };
    setState(s);
    console.log("--Intellignet Profile: state updated");
  }, [dialogId]);

  // -- Check window location --
  useEffect(() => {
    if (dialogId == 1 && window.location.href.includes(EXH_PROFILE_URL))
      setOpen(true);
  }, [dialogId, window.location.href]);

  if (isActive) {
    return (
      <>
        <StickyIcon
          handleOpen={handleClickOpen}
          color={
            isLoading
              ? theme.palette.primary.main
              : theme.palette.secondary.main
          }
        />
        <Dialog
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={handleClose}
          TransitionComponent={Transition}
          transitionDuration={{ enter: 320, exit: 600 }}
          // 👇 Props passed to Paper (modal content) position is relative
          PaperProps={{
            sx: { borderRadius: "5px", top: "0%" },
          }}
        >
          <Stack alignItems={"left"} margin={1}>
            {dialogId == 0 && (
              <WebsiteFormDialog
                website={website}
                setWebsite={setWebsite}
                handleSearch={handleSearch}
                handleClose={handleClose}
              />
            )}
            {dialogId == 1 && (
              <LoadingDialog
                progress={fakeProgress}
                setProgress={setFakeProgress}
                ms={estimatedLoadingTime}
                handleClose={handleClose}
                isClosable={!window.location.href.includes(EXH_PROFILE_URL)}
              />
            )}
            {dialogId == 2 && (
              <SuccessDialog
                handleClose={handleClose}
                profileUrl={`profile/${eveId}/${exhId}`}
              />
            )}
            {dialogId == 3 && (
              <ErrorDialog
                handlePrevious={handlePrevious}
                handleClose={handleClose}
                profileUrl={`profile/${eveId}/${exhId}`}
              />
            )}
          </Stack>
        </Dialog>
        <Notification
          openSnackbar={openSnackbar}
          handleCloseSnackbar={handleCloseSnackbar}
        />
      </>
    );
  } else return;
}
